"use client";

import { c as _c } from "react/compiler-runtime";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useCallback, useTransition } from "react";
import { patchURLArrays } from "@/utils/patchUrlArrays";

/**
 * Parse a single parameter value from URL
 */
function parseParam<T>(value: string | null, defaultValue: T): T {
  if (value === null) return defaultValue;

  // Handle boolean values
  if (typeof defaultValue === "boolean") {
    return (value === "true") as unknown as T;
  }

  // Handle number values
  if (typeof defaultValue === "number") {
    const parsed = parseInt(value);
    return (isNaN(parsed) ? defaultValue : parsed) as unknown as T;
  }

  // Handle array values (for range parameters like [min, max])
  if (Array.isArray(defaultValue)) {
    try {
      const parsed = value.split(",").map(v => {
        const num = parseFloat(v.trim());
        return isNaN(num) ? 0 : num;
      });
      return (parsed.length === defaultValue.length ? parsed : defaultValue) as unknown as T;
    } catch {
      return defaultValue;
    }
  }
  return value as unknown as T;
}

/**
 * Stringify a parameter value for URL
 */
function stringifyParam<T>(value: T): string {
  if (Array.isArray(value)) {
    return value.join(",");
  }
  return String(value);
}

/**
 * Hook for managing single URL parameter state
 *
 * @param key - URL parameter name
 * @param defaultValue - Default value when parameter is not present
 * @param options - Optional configuration
 * @returns [value, setValue, isPending]
 */
export function useURLState(key, defaultValue, options) {
  const $ = _c(15);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  let t0;
  if ($[0] !== defaultValue || $[1] !== key || $[2] !== searchParams) {
    t0 = parseParam(searchParams.get(key), defaultValue);
    $[0] = defaultValue;
    $[1] = key;
    $[2] = searchParams;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const value = t0;
  let t1;
  if ($[4] !== defaultValue || $[5] !== key || $[6] !== options?.resetKeys || $[7] !== pathname || $[8] !== router || $[9] !== searchParams) {
    t1 = newValue => {
      startTransition(() => {
        const params = new URLSearchParams(searchParams);
        options?.resetKeys?.forEach(resetKey => params.delete(resetKey));
        if (newValue === defaultValue || Array.isArray(newValue) && newValue.length === 0 || Array.isArray(defaultValue) && Array.isArray(newValue) && newValue.every((v, i) => v === defaultValue[i])) {
          params.delete(key);
        } else {
          params.set(key, stringifyParam(newValue));
        }
        router.push(`${pathname}?${params.toString()}`, {
          scroll: false
        });
      });
    };
    $[4] = defaultValue;
    $[5] = key;
    $[6] = options?.resetKeys;
    $[7] = pathname;
    $[8] = router;
    $[9] = searchParams;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  options?.resetKeys;
  const setValue = t1;
  let t2;
  if ($[11] !== isPending || $[12] !== setValue || $[13] !== value) {
    t2 = [value, setValue, isPending];
    $[11] = isPending;
    $[12] = setValue;
    $[13] = value;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}

/**
 * Hook for managing array URL parameter state (multiple values for same key)
 *
 * @param key - URL parameter name
 * @returns [values, setValues, isPending]
 */
export function useURLArrayState(key, options) {
  const $ = _c(13);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  let t0;
  if ($[0] !== key || $[1] !== searchParams) {
    t0 = searchParams.getAll(key);
    $[0] = key;
    $[1] = searchParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const values = t0;
  let t1;
  if ($[3] !== key || $[4] !== options?.resetKeys || $[5] !== pathname || $[6] !== router || $[7] !== searchParams) {
    t1 = newValues => {
      startTransition(() => {
        const params = new URLSearchParams(searchParams);
        options?.resetKeys?.forEach(resetKey => params.delete(resetKey));
        params.delete(key);
        if (newValues.length > 0) {
          newValues.forEach(value => {
            if (value && value.trim()) {
              params.append(key, value.trim());
            }
          });
        }
        router.push(`${pathname}?${params.toString()}`, {
          scroll: false
        });
      });
    };
    $[3] = key;
    $[4] = options?.resetKeys;
    $[5] = pathname;
    $[6] = router;
    $[7] = searchParams;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  options?.resetKeys;
  const setValues = t1;
  let t2;
  if ($[9] !== isPending || $[10] !== setValues || $[11] !== values) {
    t2 = [values, setValues, isPending];
    $[9] = isPending;
    $[10] = setValues;
    $[11] = values;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
}

/**
 * Hook for managing range URL parameter state (e.g., price range, year range)
 *
 * @param key - URL parameter name
 * @param defaultRange - Default range values [min, max]
 * @returns [range, setRange, isPending]
 */
export function useURLRangeState(key, defaultRange, options) {
  const $ = _c(15);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  let t0;
  if ($[0] !== defaultRange || $[1] !== key || $[2] !== searchParams) {
    const getRangeFromParams = () => {
      const values = searchParams.getAll(key);
      if (values.length === 2 && values[0] && values[1]) {
        const min = parseFloat(values[0]);
        const max = parseFloat(values[1]);
        if (!isNaN(min) && !isNaN(max)) {
          return [min, max];
        }
      }
      return defaultRange;
    };
    t0 = getRangeFromParams();
    $[0] = defaultRange;
    $[1] = key;
    $[2] = searchParams;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const range = t0;
  let t1;
  if ($[4] !== defaultRange || $[5] !== key || $[6] !== options?.resetKeys || $[7] !== pathname || $[8] !== router || $[9] !== searchParams) {
    t1 = newRange => {
      startTransition(() => {
        const params = new URLSearchParams(searchParams);
        options?.resetKeys?.forEach(resetKey => params.delete(resetKey));
        params.delete(key);
        if (newRange[0] !== defaultRange[0] || newRange[1] !== defaultRange[1]) {
          params.append(key, newRange[0].toString());
          params.append(key, newRange[1].toString());
        }
        router.push(`${pathname}?${params.toString()}`, {
          scroll: false
        });
      });
    };
    $[4] = defaultRange;
    $[5] = key;
    $[6] = options?.resetKeys;
    $[7] = pathname;
    $[8] = router;
    $[9] = searchParams;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  options?.resetKeys;
  const setRange = t1;
  let t2;
  if ($[11] !== isPending || $[12] !== range || $[13] !== setRange) {
    t2 = [range, setRange, isPending];
    $[11] = isPending;
    $[12] = range;
    $[13] = setRange;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}

/**
 * Hook for toggling a single filter value in an array
 *
 * @param key - URL parameter name
 * @param value - Value to toggle
 * @returns [isSelected, toggle, isPending]
 */
export function useURLFilterToggle(key, value) {
  const $ = _c(12);
  const [values, setValues, isPending] = useURLArrayState(key);
  let t0;
  if ($[0] !== value || $[1] !== values) {
    t0 = values.includes(value);
    $[0] = value;
    $[1] = values;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const isSelected = t0;
  let t1;
  if ($[3] !== isSelected || $[4] !== setValues || $[5] !== value || $[6] !== values) {
    t1 = () => {
      if (isSelected) {
        setValues(values.filter(v => v !== value));
      } else {
        setValues([...values, value]);
      }
    };
    $[3] = isSelected;
    $[4] = setValues;
    $[5] = value;
    $[6] = values;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const toggle = t1;
  let t2;
  if ($[8] !== isPending || $[9] !== isSelected || $[10] !== toggle) {
    t2 = [isSelected, toggle, isPending];
    $[8] = isPending;
    $[9] = isSelected;
    $[10] = toggle;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}

/**
 * Hook for clearing all URL parameters (reset filters)
 *
 * @param preserveKeys - Keys to preserve when clearing (e.g., ['q', 'page'])
 * @returns [clearAll, isPending]
 */
export function useURLClearAll(t0) {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const preserveKeys = t1;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  let t2;
  if ($[2] !== pathname || $[3] !== preserveKeys || $[4] !== router || $[5] !== searchParams) {
    t2 = () => {
      startTransition(() => {
        const params = new URLSearchParams();
        preserveKeys.forEach(key => {
          const value = searchParams.get(key);
          if (value) {
            params.set(key, value);
          }
        });
        const queryString = params.toString();
        router.push(`${pathname}${queryString ? `?${queryString}` : ""}`, {
          scroll: false
        });
      });
    };
    $[2] = pathname;
    $[3] = preserveKeys;
    $[4] = router;
    $[5] = searchParams;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const clearAll = t2;
  let t3;
  if ($[7] !== clearAll || $[8] !== isPending) {
    t3 = [clearAll, isPending];
    $[7] = clearAll;
    $[8] = isPending;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

/**
 * Hook to delete specific URL query keys atomically (single navigation).
 */
export function useURLDeleteKeys(keys) {
  const $ = _c(8);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  let t0;
  if ($[0] !== keys || $[1] !== pathname || $[2] !== router || $[3] !== searchParams) {
    t0 = () => {
      startTransition(() => {
        const params = new URLSearchParams(searchParams);
        keys.forEach(key => params.delete(key));
        const qs = params.toString();
        router.push(`${pathname}${qs ? `?${qs}` : ""}`, {
          scroll: false
        });
      });
    };
    $[0] = keys;
    $[1] = pathname;
    $[2] = router;
    $[3] = searchParams;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const clearKeys = t0;
  let t1;
  if ($[5] !== clearKeys || $[6] !== isPending) {
    t1 = [clearKeys, isPending];
    $[5] = clearKeys;
    $[6] = isPending;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}

/** Update related array filters in one navigation, preserving unrelated parameters. */
export function useURLArrayPatch(t0) {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const resetKeys = t1;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  let t2;
  if ($[2] !== pathname || $[3] !== resetKeys || $[4] !== router || $[5] !== searchParams) {
    t2 = updates => {
      startTransition(() => {
        const params = patchURLArrays(searchParams.toString(), updates, resetKeys);
        router.push(`${pathname}?${params.toString()}`, {
          scroll: false
        });
      });
    };
    $[2] = pathname;
    $[3] = resetKeys;
    $[4] = router;
    $[5] = searchParams;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const update = t2;
  let t3;
  if ($[7] !== isPending || $[8] !== update) {
    t3 = [update, isPending];
    $[7] = isPending;
    $[8] = update;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}